/**
 * Module-level in-flight + TTL cache so identical GETs share one network call
 * across React Strict Mode remounts and multiple page/components.
 */

const store = new Map();

/**
 * @param {string} key
 * @param {() => Promise<any>} loader
 * @param {{ ttlMs?: number, force?: boolean }} [opts]
 */
export function cachedRequest(key, loader, opts = {}) {
  const ttlMs = opts.ttlMs ?? 60_000;
  const force = Boolean(opts.force);
  const now = Date.now();
  const hit = store.get(key);

  if (!force && hit?.value !== undefined && now - hit.at < ttlMs) {
    return Promise.resolve(hit.value);
  }
  if (!force && hit?.promise) {
    return hit.promise;
  }

  const promise = Promise.resolve()
    .then(loader)
    .then((value) => {
      store.set(key, { value, at: Date.now(), promise: null });
      return value;
    })
    .catch((err) => {
      const cur = store.get(key);
      if (cur?.promise === promise) {
        store.set(key, { value: cur.value, at: cur.at || 0, promise: null });
      }
      throw err;
    });

  store.set(key, {
    value: hit?.value,
    at: hit?.at || 0,
    promise,
  });
  return promise;
}

export function peekCachedRequest(key) {
  const hit = store.get(key);
  return hit?.value;
}

export function clearCachedRequest(key) {
  if (key == null) store.clear();
  else store.delete(key);
}
